import { useCallback } from "react";

import { Box, Link } from "@mui/material";

import { useCloudConnect } from "../../Components/hooks/useCloudConnect";
import { useIsFeatureEntitled } from "../../Context/TierProvider";
import { type Customer } from "../../types/Customer";
import mixpanel from "../../utils/mixpanel";
import { PermissionsAlert } from "./PermissionsAlert";
import { isRealTimeAnomaliesEnabled } from "./utils/realTimeUtils";

type RealTimeAnomalyEligibilityAlertProps = {
  customer: Customer;
};

const RealTimeAnomalyEligibilityAlert = ({ customer }: RealTimeAnomalyEligibilityAlertProps) => {
  const [cloudConnectDocs] = useCloudConnect(customer);
  const isEligible = useIsFeatureEntitled("governance:costAnomalies:realTime");

  const isRealTimeEnabled = isRealTimeAnomaliesEnabled(cloudConnectDocs);

  const handleEnableClick = useCallback(() => {
    mixpanel.track("anomalies.real-time.enable.click");
  }, []);

  const handleHelpLinkClick = useCallback(() => {
    mixpanel.track("anomalies.real-time.help-link.click");
  }, []);

  if (!isEligible || isRealTimeEnabled) {
    return null;
  }

  return (
    <Box sx={{ mt: 6 }}>
      <PermissionsAlert
        link={`/customers/${customer.id}/integrate/aws/link?openRealTimeAnomaly=1`}
        title="Enable real-time anomalies"
        onClick={handleEnableClick}
      >
        {"Catch EC2 and RDS anomalies much sooner by enabling real-time detection. "}
        <Link
          href="https://help.doit.com/docs/governance/cloud-anomalies/real-time-anomaly-detection#enable-on-a-new-account"
          target="_blank"
          sx={{ cursor: "pointer", color: "inherit" }}
          onClick={handleHelpLinkClick}
        >
          Click here
        </Link>
        {" to learn how."}
      </PermissionsAlert>
    </Box>
  );
};

export { RealTimeAnomalyEligibilityAlert };
